import * as THREE from "three";
import SceneGeometry from "../sceneObject/SceneGeometry";
import { getSceneObjectStyle, getSceneObjectStyleName, SceneObjectStyle, SceneObjectStyleEnum } from "../sceneObject/SceneObjectStyle";
import LengthDimensionData from "../sceneObjectData/LengthDimensionData";
import SceneObjectData from "../sceneObjectData/SceneObjectData";
import { Font } from "three/examples/jsm/Addons.js";
import FontProvider from "../../logic/FontProvider";
import LineSegment3d from "../../math/linearAlgebra/lineSegment3d";
import { updateDoubleSidedText } from "../../utils/annotationUtils";
import { sceneUnitsFromMm } from "../../utils/scaler";

export default abstract class LengthDimensionSubGeometryBase extends SceneGeometry<LengthDimensionData> {
    
    protected static triangleScale = 0.06; // Scale value for the triangle mesh
    preferredAnnotationY: THREE.Vector3 = new THREE.Vector3(0, 1, 0); // Preferred Y-axis for the annotation text
    extensionAmount: number = LengthDimensionSubGeometryBase.triangleScale * 0.4; // Amount to extend extension lines beyond tips of the arrows

    textFont: Font | undefined = undefined; // Font for the text mesh
    textGroup: THREE.Group = new THREE.Group();
    textMaterial: THREE.MeshBasicMaterial = LengthDimensionSubGeometryBase.getTextMaterial(this.style);

    private static textMaterialCache = new WeakMap<SceneObjectStyle, THREE.MeshBasicMaterial>();

    protected static getTextMaterial(style: SceneObjectStyle): THREE.MeshBasicMaterial {
        let mat = LengthDimensionSubGeometryBase.textMaterialCache.get(style);
        if (!mat) {
            mat = new THREE.MeshBasicMaterial({ color: style.color, side: THREE.FrontSide });
            mat.name = `LengthDim-${getSceneObjectStyleName(style)}-Text`;
            LengthDimensionSubGeometryBase.textMaterialCache.set(style, mat);
        }
        return mat;
    }

    endMarkerFlipDistance: number = sceneUnitsFromMm(300); // Distance in mm at which the end marker flips direction

    constructor() {
        super();
        this.textFont = FontProvider.getInstanceSync().arial;
        this.textGroup.name = "TextGroup";
    }

    // forceUpdate: Update geometry even if data has now changed
    updateGeometry(data: SceneObjectData, forceUpdate: boolean = false): boolean {
        if (!(data instanceof LengthDimensionData)) {
            return false;
        }

        const lengthData = data as LengthDimensionData;
        let isUpdated = this.updateLineDimensions(lengthData, forceUpdate); // Update line dimensions

        // Update data reference only if the geometry was updated            
        if (isUpdated) {
            this.updateText(lengthData); // Update the text mesh
            this.data = lengthData.clone(); // Update the data reference
        }

        return isUpdated;
    }

    abstract updateLineDimensions(newData: LengthDimensionData, forceUpdate: boolean): boolean;

    static updateEndMarker(markerMesh: THREE.Mesh, position: THREE.Vector3, direction: THREE.Vector3) {
        const triangleY = new THREE.Vector3(0, 1, 0);

        const rotation = new THREE.Quaternion().setFromUnitVectors(triangleY, direction); // Calculate the rotation from lineDir to Y-axis

        markerMesh.position.copy(position); 
        markerMesh.quaternion.copy(rotation); 
    }

    onAnnotationDirectionChange(rotationOffset: number): void {
        const newAnnotationY = new THREE.Vector3(0,1,0);
        newAnnotationY.applyAxisAngle(new THREE.Vector3(0,0,1), rotationOffset);
        this.preferredAnnotationY = newAnnotationY;
        if (this.data)
            this.updateGeometry(this.data, true);
    }

    alignWithPreferredAnnotationDir(up: THREE.Vector3) : THREE.Vector3 {

        const zAxis = new THREE.Vector3(0,0,1);
        const preferredDirs = [ this.preferredAnnotationY.clone().applyAxisAngle(zAxis, Math.PI/2) ,  // Preferred annotation y - 90 degrees counter clockwise
                                this.preferredAnnotationY,
                                zAxis ];
                
        const minAngleTo = (from : THREE.Vector3, to : THREE.Vector3) => Math.min(  from.angleTo(to), 
                                                                        from.angleTo(to.clone().negate()));

        // Find out which of the main axis up is most parallel to
        const sorted = preferredDirs.sort((a,b) =>  minAngleTo(up, a) < minAngleTo(up, b) ? -1 : 1 );

        const chosen = sorted[0];
        const invertedUp = up.clone().negate();

        // Check if we are closer to preferred dir as is, or is the inverted version closer
        return up.angleTo(chosen) < invertedUp.angleTo(chosen)
                ? up
                : invertedUp;
    }

    extendLine(start: THREE.Vector3, end: THREE.Vector3): THREE.Vector3[] {
            const lineDir = new THREE.Vector3().subVectors(end, start).normalize(); // Calculate the direction of the line
            return [start.clone(), end.clone().add(lineDir.multiplyScalar(this.extensionAmount))]; // Extend the line by the triangle scale
        }

    getOffsetPoint(point: THREE.Vector3, offset: THREE.Vector3 | null): THREE.Vector3 {
            return offset
                ? point.clone().add(offset)
                : point.clone(); 
        }

    updateText(data: LengthDimensionData) : void {
        // Update the text mesh only if we have offset
        if (data.offset.lengthSq() > 0) {


            const dimensionLine = new LineSegment3d(    this.getOffsetPoint(data.start, data.offset),
                                                                this.getOffsetPoint(data.end, data.offset)); // Get the start point with offset

            if (this.group.visible && this.textFont && (this.textGroup.children.length === 0 || data.text != this.data?.text || data.fontSize !== this.data?.fontSize))
            {
                updateDoubleSidedText(data.text, this.textFont, data.fontSize, this.textGroup, this.textMaterial); // Update the text mesh if the text is different
            }
    
            const plane = new THREE.Plane().setFromCoplanarPoints(data.start, data.end, dimensionLine.p2); // Get the normal of the plane defined by the line and the offset
            const up = dimensionLine.p1.clone().sub(data.start).normalize(); // Calculate the up vector for the text mesh

            const alignedUp = this.alignWithPreferredAnnotationDir(up) 

            //console.info(`Up ${up} became ${alignedUp}`);

            const position = this.textGroup.position.clone();
            const target = position.clone().add(plane.normal);
            const lookAtMatrix = new THREE.Matrix4().lookAt(position, target, alignedUp);

            // Set the quaternion from the lookAt matrix
            this.textGroup.quaternion.setFromRotationMatrix(lookAtMatrix);

            // Offset for the text position
            const textOffsetDir = new THREE.Vector3().subVectors(dimensionLine.p1,data.start).normalize(); // Calculate the direction of the line
            const textOffset = textOffsetDir.clone().multiplyScalar(data.fontSize); // Scale the offset
    
            this.textGroup?.position.copy(dimensionLine.midpoint()); // Set the position of the text mesh to the start point
            this.textGroup?.position.add(textOffset); // Add the offset to the text position

            this.textGroup.visible = true; // Show the text group
        }
        else {
            this.textGroup.visible = false; // Hide the text group
        }
    }

    setStyle(style: SceneObjectStyle): void {
        if (this.style === style) return;
        this.textMaterial = LengthDimensionSubGeometryBase.getTextMaterial(style);
        this.textGroup.traverse((child) => {
            if (child instanceof THREE.Mesh && child.name !== "HitBoxMesh") {
                child.material = this.textMaterial;
            }
        });
        this.style = style;
    }

    dispose(): void {
        this.group.traverse((child) => {
            if (child instanceof THREE.Mesh) {
                child.geometry.dispose();
            }
        });
        this.group.parent?.remove(this.group);
        this.group.clear();
    }

    // Extend extension line little bit beyond the head of the dimensionline arrow
    // Take direction for extension by subtracting measurement point from corresponding point of the dimension line and then normalizing vector into direction vector
    calculateExtensionLineEndPt(measurementPt: THREE.Vector3, dimensionLinePt: THREE.Vector3) : THREE.Vector3 {

        const extensionBeyondArrowHead = dimensionLinePt.clone().sub(measurementPt).normalize().multiplyScalar(this.extensionAmount);
        return dimensionLinePt.clone().add(extensionBeyondArrowHead);
    }

};